import { X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import ShareButtonsRow from './ShareButtonsRow'
import type { Store } from '../store/useStore'

interface Props {
  open: boolean
  onClose: () => void
  storeId: string
  storeName: string
  snsMedia?: Store['snsMedia']
  source: 'review' | 'payment_complete' | 'store_detail'
}

/**
 * Bottom sheet wrapping the two-tier share UI (`ShareButtonsRow`).
 *
 * Used by entry points where there is no room for an inline button row —
 * e.g. the small Share2 icon in StoreDetail's hero header. After the user
 * taps a platform, the sheet closes itself so the user lands back where
 * they were before sharing.
 */
export default function ShareSheet({
  open,
  onClose,
  storeId,
  storeName,
  snsMedia,
  source,
}: Props) {
  const { t } = useI18n()

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[430px] bg-white rounded-t-2xl p-6 pb-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
          aria-label="close"
        >
          <X size={18} className="text-text-secondary" />
        </button>

        <h3 className="font-display font-semibold text-lg text-text-primary mb-1">
          {t('review.sharePrompt')}
        </h3>
        <p className="font-body text-xs text-text-tertiary mb-5">{storeName}</p>

        <ShareButtonsRow
          storeId={storeId}
          storeName={storeName}
          snsMedia={snsMedia}
          source={source}
          onShared={onClose}
        />
      </div>
    </div>
  )
}
